import { Bindable, NotifyProperty, nameof } from "bindable-data";
import { inject, injectable } from "@launchtray/tsyringe-async";
import { INetworkClient } from "../../../../networking/INetworkClient";
import { Ticker } from "pixi.js";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { IRenterPolicyModel } from "./IRenterPolicyModel";
import { RenterPolicyConfiguration } from "./RenterPolicyConfiguration";
import { RenterPolicyCycle } from "./RenterPolicyCycle";
import { RenterPolicyPenalties } from "./RenterPolicyPenalties";
import { RenterPolicyPrviousCycle } from "./RenterPolicyPrviousCycle";
import { IPlayerModel } from "../IPlayerModel";
import { BuildingRightsType } from "../../../../networking/data/responses/user/BuildingRightsType";
import { RenterPolicyType } from "../../../popups/renterpolicy/components/RenterPolicyType";
import { RenterPolicyCycleResult } from "../../../../networking/data/responses/renterPolicy/RenterPolicyCycleResult";
import { ILatestNewsModel } from "../latestnews/ILatestNewsModel";
import { IConnectionModel } from "by/gamefactory/cityversetycoon/networking/IConnectionModel";
import { IFirstTimeExperienceModel } from "../IFirstTimeExperienceModel";

@injectable()
export class RenterPolicyModel extends Bindable implements IRenterPolicyModel {
    public readonly configuration: NotifyProperty<RenterPolicyConfiguration>;
    public readonly cycle: NotifyProperty<RenterPolicyCycle>;
    public readonly penalties: NotifyProperty<RenterPolicyPenalties>;
    public readonly previousCycle: NotifyProperty<RenterPolicyPrviousCycle>;
    public readonly endsInTime: NotifyProperty<number>;
    public readonly isLowTime: NotifyProperty<boolean>;

    private readonly networkClient: INetworkClient;
    private readonly playerModel: IPlayerModel;
    private readonly latestNews: ILatestNewsModel;
    private readonly connectionModel: IConnectionModel;
    private readonly firstTimeExperienceModel: IFirstTimeExperienceModel;
    private readonly logger: ILogger;
    private doneTime: boolean;
    private activeTimer: boolean;

    public get currentRenterPolicyType(): RenterPolicyType {
        if (this.previousCycle.value?.result === RenterPolicyCycleResult.INACTIVE_PERIOD && this.penalties.value) {
            return RenterPolicyType.NOTIFICATION_DEFAULT;
        } else {
            return this.previousCycle.value?.result === RenterPolicyCycleResult.INACTIVE_PERIOD_WITH_INSURANCE ? RenterPolicyType.DELETED_RENTED : RenterPolicyType.RENTER_POLICY;
        }
    }

    public constructor(
        @inject("INetworkClient") networkClient: INetworkClient,
        @inject("IPlayerModel") playerModel: IPlayerModel,
        @inject("ILatestNewsModel") latestNews: ILatestNewsModel,
        @inject("IConnectionModel") connectionModel: IConnectionModel,
        @inject("IFirstTimeExperienceModel") firstTimeExperienceModel: IFirstTimeExperienceModel,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.networkClient = networkClient;
        this.playerModel = playerModel;
        this.latestNews = latestNews;
        this.connectionModel = connectionModel;
        this.firstTimeExperienceModel = firstTimeExperienceModel;
        this.logger = logger;
        this.configuration = new NotifyProperty<RenterPolicyConfiguration>(this, nameof(this, "configuration"), undefined);
        this.cycle = new NotifyProperty<RenterPolicyCycle>(this, nameof(this, "cycle"), undefined);
        this.penalties = new NotifyProperty<RenterPolicyPenalties>(this, nameof(this, "penalties"), undefined);
        this.previousCycle = new NotifyProperty<RenterPolicyPrviousCycle>(this, nameof(this, "previousCycle"), undefined);
        this.endsInTime = new NotifyProperty<number>(this, nameof(this, "endsInTime"), 0);
        this.isLowTime = new NotifyProperty<boolean>(this, nameof(this, "isLowTime"), false);
        this.doneTime = true;
        this.activeTimer = false;
        document.addEventListener("visibilitychange", () => {
            if (!document.hidden && this.playerModel.buildingRights.value === BuildingRightsType.RENTER) {
                this.updateRenterPolicyStatus();
            }
        });
    }

    public async updateRenterPolicyStatus(): Promise<void> {
        if (!this.connectionModel.connected.value) {
            return;
        }
        try {
            if (this.playerModel.buildingRights.value !== BuildingRightsType.RENTER || this.firstTimeExperienceModel.tooltipsInfo.value?.renterQuestIntro.value) {
                return;
            }
            const renterPolicyStatusResult = await this.networkClient.getRenterPolicyStatus();
            if (renterPolicyStatusResult != undefined) {
                this.configuration.setValue(new RenterPolicyConfiguration(renterPolicyStatusResult.configuration));
            }
            if (renterPolicyStatusResult.cycle != undefined) {
                this.cycle.setValue(new RenterPolicyCycle(renterPolicyStatusResult.cycle));
                this.endsInTime.setValue(renterPolicyStatusResult.cycle.endsIn);
                this.isLowTime.setValue(this.cycle.value.endsIn < this.cycle.value.highlightsTimeTarget);
                this.timerChangeHandle();
            }
            if (renterPolicyStatusResult.previousCycle != undefined) {
                this.previousCycle.setValue(new RenterPolicyPrviousCycle(renterPolicyStatusResult.previousCycle));
            }
            if (renterPolicyStatusResult.penalties != undefined) {
                this.penalties.setValue(new RenterPolicyPenalties(renterPolicyStatusResult.penalties));
                await this.playerModel.loadPlayerData();
                await this.playerModel.loadBuildings(false);
            }
            await this.latestNews.getLatestNewsStatus();
        } catch (error) {
            this.logger.error(error);
        }
    }

    private timerChangeHandle(): void {
        this.doneTime = this.endsInTime.value === 0;
        if (!this.doneTime && !this.activeTimer) {
            this.activeTimer = true;
            Ticker.shared.add(this.updateHandler, this);
        }
    }

    private async updateHandler(): Promise<void> {
        if (this.doneTime) {
            return;
        }
        const diff = this.endsInTime.value - Ticker.shared.deltaMS;
        if (diff <= 0) {
            this.doneTime = true;
            this.endsInTime.setValue(0);
            Ticker.shared.remove(this.updateHandler, this);
            this.activeTimer = false;
            await this.updateRenterPolicyStatus();
        } else {
            this.endsInTime.setValue(diff);
            if (this.endsInTime.value < this.cycle.value.highlightsTimeTarget) {
                this.isLowTime.setValue(true);
            }
        }
    }
}
